import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, MagnifyingGlass, Robot, ChartLineUp, Target, Quotes,
  TrendUp, Broadcast, Sparkle, Star, Trophy, WhatsappLogo,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PublicNavbar } from "@/components/public/PublicNavbar";
import { PublicFooter } from "@/components/public/PublicFooter";
import { WhatsAppFab } from "@/components/public/WhatsAppFab";
import { PROVIDERS, SECTORS, SECTOR_QUESTIONS, waLink } from "@/lib/site";
import { BLOG_POSTS } from "@/data/blog";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, ease: "easeOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const AnimatedGauge = ({ target = 87, size = 200 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  const r = size / 2 - 14;
  const c = 2 * Math.PI * r;
  const off = c - (val / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E4E4E7" strokeWidth="12" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#059669" strokeWidth="12"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="butt" />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-heading font-black tracking-tighter leading-none text-score-good" style={{ fontSize: size * 0.32 }}>{val}</span>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-score-good mt-1">Güçlü</span>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <p className="font-mono text-xs tracking-[0.2em] uppercase text-brand mb-3">{children}</p>
);

export default function Landing() {
  const navigate = useNavigate();

  const seeDemo = async () => {
    try {
      const res = await api.get("/public/demo-report");
      if (res.data.id) navigate(`/r/${res.data.id}`);
      else navigate("/login");
    } catch {
      navigate("/login");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Organization", name: "AI Marka Radar", url: window.location.origin,
        description: "Yapay zekâ asistanlarında marka görünürlüğü ölçüm platformu." },
      { "@type": "Product", name: "AI Marka Radar",
        description: "ChatGPT, Gemini ve Perplexity üzerinde marka görünürlüğünüzü ölçün ve rakiplerinizle karşılaştırın.",
        brand: { "@type": "Brand", name: "AI Marka Radar" } },
      { "@type": "FAQPage", mainEntity: FAQ.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };

  return (
    <div className="bg-background">
      <Seo
        title="AI Marka Radar — ChatGPT, Gemini ve Perplexity'de markanız görünüyor mu?"
        description="Yapay zekâ aramalarında markanızın görünürlüğünü ölçün. ChatGPT, Gemini ve Perplexity sizi öneriyor mu, rakiplerinizle karşılaştırın. Ücretsiz ilk rapor."
        path="/"
        jsonLd={jsonLd}
      />
      <PublicNavbar />
      <WhatsAppFab />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-brand mb-5">AEO / GEO · Yapay Zekâ Marka Radar Radarı</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              Müşteriler artık yapay zekâya soruyor. Peki yapay zekâ <span className="text-brand">sizi öneriyor mu?</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed max-w-xl">
              ChatGPT, Gemini ve Perplexity üzerinde marka görünürlüğünüzü ölçün, rakiplerinizle
              karşılaştırın ve pazar payınızı yapay zekâ çağında yönetin.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="hero-primary-cta" onClick={() => navigate("/login")}
                className="flex items-center justify-center gap-2 bg-brand text-white font-medium px-7 py-3.5 hover:-translate-y-0.5 transition-transform">
                Ücretsiz Skorunu Öğren <ArrowRight size={18} weight="bold" />
              </button>
              <button data-testid="hero-secondary-cta" onClick={seeDemo}
                className="flex items-center justify-center gap-2 border border-border font-medium px-7 py-3.5 hover:border-brand transition-colors">
                Örnek Raporu Gör
              </button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="relative">
          <div className="border border-border bg-background p-8 flex flex-col items-center">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">AI Marka Radar Skoru</p>
            <AnimatedGauge target={87} size={220} />
            <div className="grid grid-cols-3 gap-3 w-full mt-8">
              {Object.entries(PROVIDERS).map(([k, p], i) => (
                <motion.div key={k}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                  className="border border-border border-t-2 p-3 text-center" style={{ borderTopColor: p.hex }}>
                  <p className="text-xs font-semibold">{p.label}</p>
                  <p className="font-heading font-black text-xl tracking-tighter mt-1" style={{ color: p.hex }}>
                    {[92, 80, 78][i]}%
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="border-y border-border bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Güvenilen Markalar</p>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 opacity-40">
            {["KLİNİK", "EMLAK", "RESTORAN", "OTEL", "HUKUK"].map((n) => (
              <span key={n} className="font-heading font-black tracking-tight text-lg text-muted-foreground">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="nasil-calisir" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <Reveal><Label>Süreç</Label>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">Nasıl çalışır?</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mt-12">
          {[
            { icon: Target, t: "Markanı ve rakiplerini gir", d: "Marka adınızı, sektörünüzü, hedef pazarınızı ve rakiplerinizi girin. 30 saniyeden kısa sürer." },
            { icon: Robot, t: "Gerçek müşteri soruları AI'lara sorulsun", d: "Sektörünüze özel buying-intent soruları ChatGPT, Gemini ve Perplexity'ye birden çok kez soruyoruz." },
            { icon: ChartLineUp, t: "Skorunu ve rakip haritanı gör", d: "Marka Radar skorunuzu, rakip karşılaştırmasını ve yapay zekânın güvendiği kaynakları tek raporda alın." },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border border-border p-8 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-heading font-black text-2xl text-muted-foreground/60">0{i + 1}</span>
                  <s.icon size={28} weight="bold" className="text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-xl">{s.t}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECTOR GRID */}
      <section id="sektorler" className="bg-muted border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal><Label>Her sektöre uygun</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">Sektörünüz ne olursa olsun</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl text-lg">Müşterileriniz yapay zekâya soruyor. İşte sektörünüzde gerçek bir müşterinin sorabileceği sorular.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {SECTORS.map((s, i) => (
              <Reveal key={s} delay={(i % 3) * 0.08}>
                <div className="border border-border bg-background p-6 h-full hover:border-brand transition-colors">
                  <h3 className="font-heading font-semibold text-lg">{s}</h3>
                  <p className="font-mono text-xs text-muted-foreground mt-3 leading-relaxed">
                    <span className="text-brand">&gt;</span> {SECTOR_QUESTIONS[s]}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REPORT SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <Reveal><Label>Rapor</Label>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">Karar verdiren tek ekran</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {[
            { icon: ChartLineUp, t: "AI Marka Radar Skoru", d: "0–100 arası tek bir skorla nerede durduğunuzu anında görün." },
            { icon: Broadcast, t: "Sağlayıcı bazında anılma oranı", d: "ChatGPT, Gemini ve Perplexity'de ayrı ayrı ne kadar göründüğünüzü ölçün." },
            { icon: Trophy, t: "Rakip karşılaştırma & Ses Payı", d: "Rakiplerinizle yan yana: anılma oranı, ortalama sıra ve ses payı." },
            { icon: Quotes, t: "AI hangi kaynaklara güveniyor?", d: "Yapay zekânın en çok atıf yaptığı kaynakları görün, oralarda yer alın." },
          ].map((f, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1}>
              <div className="border border-border p-6 flex gap-4 h-full">
                <f.icon size={30} weight="bold" className="text-brand shrink-0" />
                <div>
                  <h3 className="font-heading font-semibold text-lg">{f.t}</h3>
                  <p className="text-muted-foreground mt-1 leading-relaxed">{f.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY IMPORTANT */}
      <section className="bg-brand text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/60 mb-3">Neden önemli?</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Arama alışkanlığı klasik motorlardan yapay zekâ asistanlarına kayıyor.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
            {[
              { t: "Tek cevap devri", d: "Kullanıcılar on bağlantı yerine yapay zekânın verdiği tek cevabı okuyor ve ona göre karar veriyor." },
              { t: "Öneriler doğrudan AI'dan", d: "İnsanlar artık öneri isterken doğrudan yapay zekâya soruyor; cevapta yoksanız görünmezsiniz." },
              { t: "Rakibiniz çıkabilir", d: "Cevapta rakibiniz varsa müşteriyi o kazanır — üstelik bundan haberiniz bile olmaz." },
            ].map((b, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="border-t-2 border-white/30 pt-5">
                  <h3 className="font-heading font-bold text-2xl">{b.t}</h3>
                  <p className="text-white/70 mt-2 leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="fiyatlandirma" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <Reveal className="text-center">
          <Label>Fiyatlandırma</Label>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Size uygun planı seçin</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14 max-w-6xl mx-auto">
          {PRICING.map((tier, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={`border p-8 h-full flex flex-col ${tier.popular ? "border-brand border-t-4 md:-translate-y-4 shadow-sm" : "border-border"}`}
                data-testid={`pricing-tier-${i + 1}`}>
                {tier.popular && <span className="font-mono text-[10px] tracking-widest uppercase bg-brand text-white px-2 py-1 self-start mb-3">En Popüler</span>}
                <h3 className="font-heading font-bold text-xl">{tier.name}</h3>
                <p className="text-muted-foreground text-sm mt-1">{tier.tagline}</p>
                <p className="font-heading font-black text-3xl tracking-tight mt-4">{tier.price}</p>
                <ul className="space-y-2.5 mt-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check size={16} weight="bold" className="text-brand shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button data-testid={`pricing-tier-${i + 1}-cta`}
                  onClick={() => (tier.cta === "Ücretsiz Başla" ? navigate("/login") : (window.location.href = waLink()))}
                  className={`mt-8 w-full py-3 font-medium transition-transform hover:-translate-y-0.5 ${tier.popular ? "bg-brand text-white" : "border border-border hover:border-brand"}`}>
                  {tier.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="sss" className="bg-muted border-y border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="text-center mb-12">
            <Label>SSS</Label>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Sıkça sorulan sorular</h2>
          </Reveal>
          <div className="border border-border bg-background divide-y divide-border">
            {FAQ.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <Reveal><Label>Blog</Label>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Son yazılar</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {BLOG_POSTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <button onClick={() => navigate(`/blog/${p.slug}`)} data-testid={`blog-card-${i}`}
                className="border border-border bg-background text-left h-full flex flex-col hover:border-brand transition-colors">
                <div className="h-36 bg-brand/5 flex items-center justify-center border-b border-border">
                  <Sparkle size={32} weight="bold" className="text-brand/40" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading font-semibold text-lg leading-snug">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 flex-1">{p.excerpt}</p>
                  <span className="text-sm text-brand font-medium mt-4 inline-flex items-center gap-1">Devamını Oku <ArrowRight size={14} /></span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Yapay zekâ sizi öneriyor mu? Hemen öğrenin.
            </h2>
            <p className="text-white/70 mt-4 text-lg">İlk raporunuz ücretsiz. Kredi kartı gerekmez.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <button onClick={() => navigate("/login")} data-testid="final-cta-start"
                className="bg-background text-brand font-medium px-7 py-3.5 hover:-translate-y-0.5 transition-transform">
                Ücretsiz Başla
              </button>
              <a href={waLink()} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 border border-white/40 font-medium px-7 py-3.5 hover:bg-background/10 transition-colors">
                <WhatsappLogo size={18} weight="fill" /> WhatsApp'tan Yazın
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-accordion-item-${idx + 1}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="font-heading font-semibold">{q}</span>
        <span className="text-brand text-xl shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="px-5 pb-5 text-muted-foreground leading-relaxed -mt-1">{a}</p>}
    </div>
  );
};

const PRICING = [
  { name: "Başlangıç Raporu", tagline: "Tek seferlik keşif", price: "Ücretsiz",
    features: ["1 marka için tam rapor", "ChatGPT · Gemini · Perplexity", "Rakip karşılaştırma tablosu", "Kaynak listesi", "Paylaşılabilir link + PDF"],
    cta: "Ücretsiz Başla", popular: false },
  { name: "Aylık AI Marka Radar Yönetimi", tagline: "Sürekli takip & yönetim", price: "Teklif alın",
    features: ["Haftalık otomatik takip", "Trend ve sıralama değişimi", "İçerik & kaynak çalışması", "Rakip alarmları", "Yönetilen hizmet & destek"],
    cta: "İletişime Geç", popular: true },
  { name: "Profesyonel Radar Raporu", tagline: "Derinlemesine analiz", price: "Teklif alın",
    features: ["Genişletilmiş soru seti", "5 rakibe kadar analiz", "Çoklu dil & pazar", "Uzman yorumu & öneriler", "Öncelikli destek"],
    cta: "İletişime Geç", popular: false },
];

const FAQ = [
  { q: "Yapay zekâ sonuçlarını garanti edebilir misiniz?",
    a: "Hayır; hiçbir firma yapay zekâ cevaplarını garanti edemez. Biz görünürlüğünüzü ölçer, eksik olduğunuz kaynakları gösterir ve görünürlük ihtimalinizi artıracak çalışmaları yaparız." },
  { q: "Verilerim güvende mi?",
    a: "Girdiğiniz marka ve rakip bilgileri yalnızca raporunuzu üretmek için kullanılır. Raporlarınız hesabınıza özeldir; yalnızca siz paylaşım linki oluşturduğunuzda herkese açık olur." },
  { q: "Rapor ne kadar sürede hazır olur?",
    a: "Her soru üç sağlayıcıya birden çok kez sorulduğu için rapor genellikle birkaç dakika içinde hazır olur. Hazır olduğunda sayfa otomatik güncellenir." },
  { q: "Hangi yapay zekâ asistanları destekleniyor?",
    a: "Şu an ChatGPT, Gemini ve Perplexity destekleniyor. Google AI Overviews gibi kaynaklar ilerleyen sürümlerde eklenecektir." },
  { q: "Skor nasıl hesaplanıyor?",
    a: "Her sağlayıcıda markanızın anılma oranı ile önerildiğinde bulunduğu sıranın ağırlığı çarpılır; sağlayıcılar önem sırasına göre ağırlıklandırılıp 0–100 arası tek bir skora dönüştürülür. Yöntem raporda şeffaf biçimde gösterilir." },
];
